import { Disclosure } from '@headlessui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import { sub } from 'date-fns'
import { useEffect, useState } from 'react'

import { AdminPage, AdminPageHeader, AdminSection } from '../../common/components'
import { Select, Text } from '../../common/designs'
import type { DateRange } from '../../common/types/monitoring'
import { roundToNextHour } from '../../common/utils/dateTimeUtils'
import { PerformanceReadingTypeEnum } from '../PerformanceMonitoringAdminPanelPage/types'
import ConnectionsBarchart from './ConnectionsBarchart'
import IdentifierOccurancesHeatmap from './IdentifierOccurancesBarchart'
import PerformancePercentilesBarchart from './PerformancePercentilesLinechart'

const MonitoringOverviewAdminPanelPage = () => {
  const [daysSelected, setDaysSelected] = useState<number>(7)

  const [selectedDates, setSelectedDates] = useState<DateRange>({
    start: new Date(sub(new Date(), { days: 6 }).setHours(0, 0, 0, 0)),
    end: roundToNextHour(new Date()),
    isSelecting: false,
  })

  useEffect(() => {
    const today = new Date()
    setSelectedDates({
      start: new Date(sub(today, { days: daysSelected - 1 }).setHours(0, 0, 0, 0)),
      end: roundToNextHour(today),
      isSelecting: false,
    })
  }, [daysSelected])

  return (
    <AdminPage width='full'>
      <AdminPageHeader
        title='Monitoring Overview'
        description='Track connectivity and performance trends across the selected time range. All timestamps are shown in UTC time zone.'
        actions={
          <Select
            value={daysSelected}
            options={[
              { label: 'Last 3 Days', value: 3 },
              { label: 'Last 7 Days', value: 7 },
              { label: 'Last 14 Days', value: 14 },
              { label: 'Last 30 Days', value: 30 },
              { label: 'Last 60 Days', value: 60 },
              { label: 'Last 90 Days', value: 90 },
            ]}
            onChange={(days: number) => setDaysSelected(days)}
          />
        }
      />

      <div className='mt-8 space-y-6'>
        <ConnectionsBarchart selectedDates={selectedDates} />

        <div className='grid grid-cols-1 gap-4 2xl:grid-cols-2'>
          <PerformancePercentilesBarchart
            selectedDates={selectedDates}
            unit={PerformanceReadingTypeEnum.CPU}
            percentile={0.95}
          />
          <PerformancePercentilesBarchart
            selectedDates={selectedDates}
            unit={PerformanceReadingTypeEnum.MEMORY}
            percentile={0.95}
          />
        </div>

        <Disclosure as='div'>
          {({ open }) => (
            <AdminSection className='space-y-2'>
              <Disclosure.Button className='flex w-full flex-wrap items-center justify-between gap-2 text-left'>
                <Text size='s' color='lighterGray'>
                  Identifier occurrences can take a while to load. Open it only when needed.
                </Text>
                <span className='inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50'>
                  {open ? 'Hide' : 'Show'} identifier data
                  {open ? (
                    <ChevronUpIcon className='size-4 text-gray-500' aria-hidden />
                  ) : (
                    <ChevronDownIcon className='size-4 text-gray-500' aria-hidden />
                  )}
                </span>
              </Disclosure.Button>

              <Disclosure.Panel className='pt-1'>
                <IdentifierOccurancesHeatmap selectedDates={selectedDates} />
              </Disclosure.Panel>
            </AdminSection>
          )}
        </Disclosure>
      </div>
    </AdminPage>
  )
}

export default MonitoringOverviewAdminPanelPage

